import React, { useEffect, useMemo } from "react";
import * as S from "./styled";
import { Input } from "../index-styled";
import PT from "prop-types";
import { OvalPreloader } from "_c/preloader";
import PassInput from "../pass-input";
import Socials from "../socials";
import { useForm, FormContext } from "react-hook-form";
import { AbstractField, AbstractControlledField } from "_c/form/Field";
import { useNotify } from "@/hooks/useNotify";
import { authSchema } from "@/validation/auth";
import { detectTimezone } from "@/utils/time";
import { ProgressEmitter } from "_c/GlobalProgress/emitter";
import PhoneInput from "_c/form/PhoneInput";
import { connect } from "react-redux";
import { Selectors as IdentitySelectors } from "@/store/identityModal/selectors";
import { Selectors as LocaleSelectors } from "@/store/locale/selectors";
import { Actions as IdentityActions } from "@/store/identityModal/slice";
import { Effects as AuthEffects } from "@/store/auth/effects";
import { Actions as ModalActions } from "@/store/modals/slice";
import PhoneEmailToggler from "../PhoneEmailToggler";
import RestorePassword from "./RestorePassword";
import { ENABLE_AUTH_BY_EMAIL } from "@/config";

const Auth = ({ t, ...props }) => {
  const methods = useForm({
    validationSchema: authSchema,
    defaultValues: props.defaultValues,
  });
  const timezone = useMemo(() => {
    return detectTimezone();
  }, []);
  const { notifyError } = useNotify();
  const { handleSubmit, formState, setError, setValue, register } = methods;
  const { isSubmitting } = formState;
  const onSubmit = async (form) => {
    try {
      ProgressEmitter.start();
      await props.login(form);
      props.close();
      props.resetForm();
    } catch (e) {
      if (e === "EMAIL_NOT_FOUND") {
        setError("email", "serverError", e);
      } else if (e === "PHONE_NOT_FOUND") {
        setError("phone", "serverError", e);
      } else if (e === "BAD_PASSWORD") {
        setError("password", "serverError", e);
      } else {
        notifyError(t([`errors:${e}`, "errors:unspecified_server"]));
      }
    } finally {
      ProgressEmitter.end();
    }
  };
  useEffect(() => {
    register("strategy");
  }, [register]);
  useEffect(() => {
    setValue("strategy", props.strategy);
  }, [props.strategy, setValue]);
  if (props.showRestore) {
    return (
      <RestorePassword
        t={t}
        hideForm={() => props.changeRestorePasswordMode(false)}
      />
    );
  }
  return (
    <FormContext {...methods}>
      <S.Wrap onSubmit={handleSubmit(onSubmit)}>
        {ENABLE_AUTH_BY_EMAIL && (
          <PhoneEmailToggler
            value={props.strategy}
            onChange={props.changeStrategy}
            t={t}
          />
        )}
        <S.InputSect>
          {props.strategy === "email" && (
            <AbstractField
              t={t}
              Component={Input}
              name="email"
              type="email"
              id="auth_email"
              placeholder="Email"
            />
          )}
          {props.strategy === "phone" && (
            <AbstractControlledField
              name="phone"
              id="auth_phone"
              t={t}
              locale={props.locale}
              placeholder={t("common:phone")}
              inputComponent={Input}
              Component={PhoneInput}
            />
          )}
          <AbstractField
            Component={PassInput}
            name="password"
            id="auth_password"
            placeholder={t("login:pass")}
            t={t}
            sectStyle={{ marginBottom: 0 }}
          />
        </S.InputSect>
        {!props.showRestore && (
          <S.SubmitButton greenish large disabled={isSubmitting}>
            {isSubmitting ? (
              <OvalPreloader style={{ width: "20px" }} />
            ) : (
              t("login:enter")
            )}
          </S.SubmitButton>
        )}
        <S.ForgotPassword
          type="button"
          onClick={() => props.changeRestorePasswordMode(true)}
        >
          {t("login:pass_forget")}
        </S.ForgotPassword>
        <Socials timezone={timezone} locale={props.locale} />
      </S.Wrap>
    </FormContext>
  );
};
Auth.propTypes = {
  defaultValues: PT.object.isRequired,
  locale: PT.string,
  showRestore: PT.bool,
  strategy: PT.oneOf(["phone", "email"]).isRequired,

  changeStrategy: PT.func.isRequired,
  updateForm: PT.func.isRequired,
  resetForm: PT.func.isRequired,
  close: PT.func.isRequired,
  changeRestorePasswordMode: PT.func.isRequired,
  login: PT.func.isRequired,
  t: PT.func.isRequired,
};
export default connect(
  (state) => ({
    defaultValues: IdentitySelectors.getAuthForm(state),
    locale: LocaleSelectors.getLocale(state),
    strategy: IdentitySelectors.getStrategy(state),
  }),
  {
    updateForm: IdentityActions.updateAuthForm,
    changeStrategy: IdentityActions.changeStrategy,
    resetForm: IdentityActions.resetAuthForm,
    login: AuthEffects.login,
    close: ModalActions.closeAuth,
  },
)(Auth);
